import { userApiClient } from '../userApiClient';
import { PassTargetType, PassWalletSummary } from '../models/Pass';

export interface PassEligibilityQuery {
  targetType: PassTargetType;
  /** Room id (room bookings) or lesson option id (tutor bookings). */
  targetId: string | null | undefined;
  /** Booking duration in minutes; only used to check balance on MINUTE-unit (room) passes. */
  requiredMinutes?: number;
}

/**
 * Why no pass can be used when `eligiblePasses` is empty.
 * - `insufficient_balance` — wallet has ACTIVE passes for this target, but remaining minutes
 *   are below the booking duration (room / MINUTE packs).
 * - `none` — no ACTIVE wallet pass covers this target (or wallet empty / error).
 */
export type PassEligibilityEmptyReason = 'none' | 'insufficient_balance';

export interface PassEligibilityResult {
  eligiblePasses: PassWalletSummary[];
  emptyReason: PassEligibilityEmptyReason | null;
}

/**
 * Resolves the wallet passes a user can spend on a given booking target.
 *
 * Uses `targetIds` from `GET /v1/user/passes` so eligibility stays valid after the host
 * deactivates/archives the original offer (public offer lookup is ACTIVE-only).
 */
export async function fetchEligibleWalletPasses({
  targetType,
  targetId,
  requiredMinutes,
}: PassEligibilityQuery): Promise<PassEligibilityResult> {
  if (!targetId) {
    return { eligiblePasses: [], emptyReason: null };
  }

  let wallet: PassWalletSummary[];
  try {
    wallet = await userApiClient.listWalletPasses({ targetType });
  } catch (error) {
    console.error('Error fetching wallet passes for eligibility check:', error);
    return { eligiblePasses: [], emptyReason: 'none' };
  }

  const activeWithBalance = wallet.filter(
    (pass) => pass.status === 'ACTIVE' && pass.remainingQuantity > 0
  );
  const coversTarget = activeWithBalance.filter((pass) =>
    (pass.targetIds ?? []).includes(targetId)
  );

  if (coversTarget.length === 0) {
    return { eligiblePasses: [], emptyReason: 'none' };
  }

  const eligiblePasses = coversTarget.filter((pass) => {
    if (pass.unitType === 'MINUTE' && requiredMinutes != null) {
      return pass.remainingQuantity >= requiredMinutes;
    }
    return true;
  });

  if (eligiblePasses.length > 0) {
    return { eligiblePasses, emptyReason: null };
  }

  const blockedOnlyByBalance = coversTarget.some(
    (pass) => pass.unitType === 'MINUTE' && requiredMinutes != null
  );

  return {
    eligiblePasses: [],
    emptyReason: blockedOnlyByBalance ? 'insufficient_balance' : 'none',
  };
}
